import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { ReferralStats, WhitelistStatus } from "@/types";
import { LeaderboardUserEntry } from "@crypto-trading/common/browser";
import { Loader2 } from "lucide-react";
import {
  useReferralStats,
  useIsUserReferred,
  useWhitelistStatus,
  useJoinWithReferral,
} from "@/hooks/useReferrals";
import { useUserRank, useLeaderboard } from "@/hooks/useLeaderboard";
import {
  extractReferralCode,
  copyToClipboard,
  shareReferralCode,
} from "@/api/utils";
import { getAuthMode } from "@/utils/telegram-environment";

interface TelegramUser {
  id: number;
  username?: string;
  firstName?: string;
  lastName?: string;
}

interface ComposedReferralData {
  referralStats: ReferralStats;
  leaderboard: LeaderboardUserEntry[];
  whitelistStatus: WhitelistStatus;
}

interface ReferralsContextType {
  data: ComposedReferralData | null;
  loading: boolean;
  error: string | null;
  userId: string | undefined;
  user: TelegramUser | undefined;
  isWhitelisted: boolean;
  isReferred: boolean;
  showSuccessDialog: boolean;
  setShowSuccessDialog: (show: boolean) => void;
  loadData: () => Promise<void>;
  handleReferralCodeSubmit: (code: string) => Promise<void>;
  handleSkipReferral: () => Promise<void>;
  handleCopyCode: () => Promise<void>;
  handleShareCode: () => void;
}

const ReferralsContext = createContext<ReferralsContextType | undefined>(
  undefined
);

export const useReferrals = () => {
  const context = useContext(ReferralsContext);
  if (!context) {
    throw new Error("useReferrals must be used within ReferralsProvider");
  }
  return context;
};

interface ReferralsProviderProps {
  children: React.ReactNode;
}

export const ReferralsProvider: React.FC<ReferralsProviderProps> = ({
  children,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [mutationLoading, setMutationLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const launchParams = useLaunchParams();
  const user = launchParams.initData?.user;
  const userId = user?.id?.toString();

  // Separate React Query hooks for each data type
  const {
    data: whitelistStatus,
    isLoading: isLoadingWhitelist,
    refetch: refetchWhitelist,
  } = useWhitelistStatus(userId);
  const {
    data: referralStats,
    isLoading: isLoadingStats,
    refetch: refetchStats,
  } = useReferralStats(userId);
  const {
    data: isReferredData,
    isLoading: isLoadingReferred,
    refetch: refetchReferred,
  } = useIsUserReferred(userId);

  // Leaderboard hooks from the new leaderboard system
  const {
    data: leaderboard,
    isLoading: isLoadingLeaderboard,
    refetch: refetchLeaderboard,
  } = useLeaderboard(10);
  const {
    data: userRank,
    isLoading: isLoadingRank,
    refetch: refetchRank,
  } = useUserRank(userId);

  const joinMutation = useJoinWithReferral();

  // Compose data from separate queries
  const data: ComposedReferralData | null = useMemo(() => {
    if (
      !referralStats ||
      !leaderboard ||
      !whitelistStatus ||
      userRank === undefined
    ) {
      return null;
    }

    return {
      referralStats: {
        ...referralStats,
        userRank,
        isReferred: isReferredData ?? false,
      },
      leaderboard,
      whitelistStatus,
    };
  }, [referralStats, leaderboard, whitelistStatus, userRank, isReferredData]);

  const isLoading =
    isLoadingStats ||
    isLoadingLeaderboard ||
    isLoadingReferred ||
    isLoadingWhitelist ||
    isLoadingRank;
  const isWhitelisted = whitelistStatus?.isWhitelisted || false;
  const isReferred = isReferredData || false;

  // Smart redirect based on user status
  useEffect(() => {
    if (!data) return;

    if (location.pathname !== "/referral-entry" && !isWhitelisted) {
      // Users who are neither whitelisted nor referred stay on /referral-entry
      console.log(
        "[ReferralsProvider] User is neither whitelisted nor referred, redirecting to referral-entry"
      );
      navigate("/referral-entry", { replace: true });
    }

    // Only redirect from referral-entry or root path
    if (location.pathname === "/referral-entry" || location.pathname === "/") {
      if (isWhitelisted) {
        // Whitelisted users go to dashboard
        console.log(
          "[ReferralsProvider] Whitelisted user detected, redirecting to referrals"
        );
        navigate("/referrals", { replace: true });
      } else if (isReferred) {
        // Referred users (but not whitelisted) go to referrals screen
        console.log(
          "[ReferralsProvider] Referred user detected, redirecting to referrals"
        );
        navigate("/referrals", { replace: true });
      }
      // Users who are neither whitelisted nor referred stay on /referral-entry
    }
  }, [data, isWhitelisted, isReferred, location.pathname, navigate]);

  const loadData = async () => {
    if (!userId) return;
    await Promise.all([
      refetchStats(),
      refetchLeaderboard(),
      refetchReferred(),
      refetchWhitelist(),
      refetchRank(),
    ]);
  };

  const handleReferralCodeSubmit = async (code: string) => {
    if (!userId) {
      setError("User ID not available from Telegram");
      throw new Error("No user ID");
    }

    try {
      setMutationLoading(true);
      setError(null);
      await joinMutation.mutateAsync({
        telegramId: userId,
        referralCode: code,
      });
      setError(null);
      localStorage.setItem("referral-code", code);
      setShowSuccessDialog(true);
    } catch (err) {
      console.error("Error joining with referral code:", err);
      if ((err as any)?.status === 401) {
        window.location.href = "/referral-entry";
      }
      setError(
        (err as Error)?.message ||
          "Failed to apply referral code. Please try again."
      );
      throw err;
    } finally {
      setMutationLoading(false);
    }
  };

  const handleSkipReferral = async () => {
    if (!userId) {
      setError("User ID not available from Telegram");
      throw new Error("No user ID");
    }

    try {
      setMutationLoading(true);
      setError(null);
      await joinMutation.mutateAsync({ telegramId: userId });
    } catch (err) {
      console.error("Error creating user:", err);
      setError("Failed to create account. Please try again.");
      throw err;
    } finally {
      setMutationLoading(false);
    }
  };

  const handleCopyCode = async () => {
    const referralLink = data?.referralStats?.referralLink;
    if (referralLink && typeof referralLink === "string") {
      const referralCode = extractReferralCode(referralLink);
      const success = await copyToClipboard(referralCode);
      if (success && globalThis.Telegram?.WebApp?.HapticFeedback) {
        globalThis.Telegram.WebApp.HapticFeedback.notificationOccurred(
          "success"
        );
      }
    }
  };

  const handleShareCode = () => {
    const referralLink = data?.referralStats?.referralLink;
    if (referralLink && typeof referralLink === "string") {
      if (getAuthMode() === "browser") {
        console.log("Sharing referral link in browser");
        if (navigator.share) {
          navigator
            .share({
              title: "Join Trigger",
              text: "Join Trigger - The fastest trading alerts platform! 🚀",
              url: referralLink,
            })
            .catch(console.error);
        } else {
          window.open(referralLink, "_blank");
        }
        return;
      }

      const referralCode = extractReferralCode(referralLink);
      shareReferralCode(referralCode);
      if (globalThis.Telegram?.WebApp?.HapticFeedback) {
        globalThis.Telegram.WebApp.HapticFeedback.impactOccurred("light");
      }
    }
  };

  const value: ReferralsContextType = useMemo(
    () => ({
      data: data || null,
      loading: isLoading || mutationLoading,
      error,
      userId,
      user,
      isWhitelisted,
      isReferred,
      showSuccessDialog,
      setShowSuccessDialog,
      loadData,
      handleReferralCodeSubmit,
      handleSkipReferral,
      handleCopyCode,
      handleShareCode,
    }),
    [
      data,
      isLoading,
      mutationLoading,
      error,
      userId,
      user,
      isWhitelisted,
      isReferred,
      showSuccessDialog,
    ]
  );

  // Show loading only on initial load
  if (isLoading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto mb-4" />
          <p className="text-text-primary">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ReferralsContext.Provider value={value}>
      {children}
    </ReferralsContext.Provider>
  );
};
